<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import MotorReservaRoomsAPI from 'dashboard/api/motorReservaRooms';

const props = defineProps({
  room: { type: Object, default: () => ({}) },
  isSubmitting: { type: Boolean, default: false },
  parentRooms: { type: Array, default: () => [] },
});

const emit = defineEmits(['submit', 'close']);
const { t } = useI18n();

const activeTab = ref('general');

// Dados Gerais
const name = ref(props.room.name || '');
const description = ref(props.room.description || '');
const richDescription = ref(props.room.rich_description || '');
const youtubeUrl = ref(props.room.youtube_url || '');
const minOccupancy = ref(props.room.min_occupancy || 1);
const maxOccupancy = ref(props.room.max_occupancy || 2);
const maxAdults = ref(props.room.max_adults || 2);
const maxChildren = ref(props.room.max_children || 0);
const allowsSmokers = ref(props.room.allows_smokers || false);
const available = ref(props.room.available !== false);
const active = ref(props.room.active !== false);
const totalUnits = ref(props.room.total_units || 1);

// Novos campos TDF
const roomType = ref(props.room.room_type || '');
const roomNumber = ref(props.room.room_number || '');
const slogan = ref(props.room.slogan || '');
const status = ref(props.room.status || 'active');
const parentId = ref(props.room.parent_id || null);
const externalRef = ref(props.room.external_ref || '');

// Fotos
const photos = ref([...(props.room.photos || [])]);
const isUploadingPhoto = ref(false);
const uploadProgress = ref({ current: 0, total: 0 });

// Comodidades
const selectedAmenities = ref([...(props.room.amenities_list || [])]);
const amenitySearch = ref('');

// Tipos de Cama
const bedTypes = ref([...(props.room.bed_types || [])]);
const newBedType = ref('');
const newBedQty = ref(1);

const roomTypeOptions = [
  'Apartamento',
  'Bar',
  'Cabana',
  'Camping',
  'Casas',
  'Chalés',
  'Compartilhados',
  'Day Use',
  'Espaços',
  'Flats',
  'Hostel',
  'Hotéis',
  'Loft',
  'Pousadas',
  'Quarto',
  'Restaurantes',
  'Sala de Eventos',
  'Salas',
  'Sítios',
  'Suíte',
  'Terreno',
  'Unidade Habitacional',
];

const statusOptions = [
  { value: 'active', label: 'Ativo' },
  { value: 'inactive', label: 'Inativo' },
  { value: 'sold', label: 'Vendido' },
  { value: 'rented', label: 'Locado' },
];

const availableParents = computed(() =>
  props.parentRooms.filter(r => r.id !== props.room.id)
);

// Fotos - Upload
const onFileSelected = async event => {
  const files = Array.from(event.target.files);
  if (!files.length || !props.room.id) return;
  isUploadingPhoto.value = true;
  uploadProgress.value = { current: 0, total: files.length };
  try {
    for (const file of files) {
      uploadProgress.value.current++;
      const response = await MotorReservaRoomsAPI.addPhoto(props.room.id, file);
      photos.value = response.data.photos || [];
    }
    useAlert(`${files.length} foto(s) adicionada(s) com sucesso.`);
  } catch (error) {
    useAlert('Erro ao fazer upload de foto.');
  } finally {
    isUploadingPhoto.value = false;
    uploadProgress.value = { current: 0, total: 0 };
    event.target.value = '';
  }
};

const removePhoto = async photo => {
  if (!props.room.id) return;
  try {
    const response = await MotorReservaRoomsAPI.removePhoto(
      props.room.id,
      photo.id
    );
    photos.value = response.data.photos || [];
    useAlert('Foto removida com sucesso.');
  } catch (error) {
    useAlert('Erro ao remover foto.');
  }
};

// Fotos - Reordenar
const movePhoto = async (index, direction) => {
  const newIndex = index + direction;
  if (newIndex < 0 || newIndex >= photos.value.length) return;
  const arr = [...photos.value];
  [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
  photos.value = arr;
  try {
    await MotorReservaRoomsAPI.reorderPhotos(
      props.room.id,
      arr.map(p => p.id)
    );
  } catch (error) {
    useAlert('Erro ao reordenar fotos.');
  }
};

// Fotos - Toggle anúncio
const togglePhotoAd = async photo => {
  photo.show_in_ad = !photo.show_in_ad;
  try {
    await MotorReservaRoomsAPI.reorderPhotos(
      props.room.id,
      photos.value.map(p => p.id)
    );
  } catch (error) {
    useAlert('Erro ao atualizar foto.');
  }
};

// Comodidades
const toggleAmenity = amenity => {
  const i = selectedAmenities.value.indexOf(amenity);
  if (i >= 0) selectedAmenities.value.splice(i, 1);
  else selectedAmenities.value.push(amenity);
};

// Tipos de Cama
const addBedType = () => {
  if (newBedType.value.trim()) {
    bedTypes.value.push({
      name: newBedType.value.trim(),
      quantity: newBedQty.value,
    });
    newBedType.value = '';
    newBedQty.value = 1;
  }
};
const removeBedType = idx => bedTypes.value.splice(idx, 1);

const isFormValid = computed(() => name.value.trim().length > 0);

const handleSubmit = () => {
  if (!isFormValid.value) return;
  emit('submit', {
    motor_reserva_room: {
      name: name.value.trim(),
      description: description.value,
      rich_description: richDescription.value,
      youtube_url: youtubeUrl.value,
      min_occupancy: Number(minOccupancy.value),
      max_occupancy: Number(maxOccupancy.value),
      max_adults: Number(maxAdults.value),
      max_children: Number(maxChildren.value),
      allows_smokers: allowsSmokers.value,
      available: available.value,
      active: active.value,
      total_units: Number(totalUnits.value),
      amenities_list: selectedAmenities.value,
      bed_types: bedTypes.value,
      room_type: roomType.value,
      room_number: roomNumber.value,
      slogan: slogan.value,
      status: status.value,
      parent_id: parentId.value || null,
      external_ref: externalRef.value || null,
    },
  });
};

const unitsCount = computed(() => (props.room.units || []).length);
const activeUnitsCount = computed(
  () => (props.room.units || []).filter(u => u.status === 'active').length
);

const tabs = computed(() => [
  { key: 'general', label: t('MOTOR_RESERVA.ROOMS.TABS.GENERAL') },
  { key: 'photos', label: t('MOTOR_RESERVA.ROOMS.TABS.PHOTOS') },
  { key: 'amenities', label: t('MOTOR_RESERVA.ROOMS.TABS.AMENITIES') },
  { key: 'beds', label: t('MOTOR_RESERVA.ROOMS.TABS.BEDS') },
]);

const ic =
  'w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 focus:border-woot-500 focus:outline-none';

// Lista completa de comodidades do HBook
const amenitiesCatalog = [
  'A unidade é totalmente acessível para cadeira de rodas',
  'Acesso ao Lounge Executivo',
  'Acesso por área externa',
  'Acesso somente por escadas',
  'Adega',
  'Airfryer',
  'Álcool em Gel',
  'Amenidades de Banho',
  "Amenidades de Banho - L'Occitane",
  'Amenidades de Banho - Natura',
  'Amenidades de Banho - O Boticário',
  'Andares superiores acessíveis por elevador',
  'Andares superiores acessíveis somente por escada',
  'Aparelho de Fondue',
  'Aparelho de vídeo',
  'Aquecedor',
  'Aquecedor portátil',
  'Aquecimento',
  'Aquecimento à gás',
  'Aquecimento de água a gás',
  'Aquecimento solar',
  'Ar-Condicionado',
  'Ar-Condicionado Split',
  'Ar-Condicionado Split no quarto',
  'Ar-condicionado (quente e frio)',
  'Ar-condicionado individual para cada ambiente na acomodação',
  'Arara para roupas',
  'Área de estar',
  'Área de refeições',
  'Área de refeições externa',
  'Área gourmet',
  'Babyliss',
  'Banheira',
  'Banheira de hidromassagem',
  'Banheira de hidromassagem e cromoterapia',
  'Banheira ou chuveiro',
  'Banheira de imersão',
  'Banheiro acessível',
  'Banheiro adicional',
  'Banheiro compartilhado',
  'Banheiro Externo',
  'Banheiro equipado com duas duchas (aquecimento solar)',
  'Banheiro no quarto',
  'Banheiro Privativo Externo',
  'Banheiro privativo',
  'Base para iPod',
  'Berço',
  'Bidê',
  'Blu-ray player',
  'Cadeirinha para refeições (crianças)',
  'Cafeteira',
  'Caiaque',
  'Calefação',
  'Cama auxiliar',
  'Cama Box Solteiro',
  'Cama box casal',
  'Cama Box Queen',
  'Cama box king-size',
  'Cama box super King Size',
  'Cama de casal',
  'Cama de Solteiro',
  'Cama dobrável',
  'Cama king-size',
  'Cama Queen Size',
  'Cama super king-size',
  'Camas extralongas (+ de 2 metros)',
  'Canais a cabo',
  'Canais pay-per-view',
  'Canais via satélite',
  'CD player',
  'Cervejeira',
  'Chaleira elétrica',
  'Chaleira/cafeteira',
  'Chapinha de cabelo',
  'Chinelos',
  'Choppeira',
  'Churrasqueira',
  'Chuveiro',
  'Chuveiro Elétrico',
  'Chuveiro Quente / Frio',
  'Climatizador',
  'Closet',
  'Cobertor elétrico',
  'Cofre',
  'Cofre eletrônico',
  'Cofre para notebook',
  'Colchão Magnético',
  'Colchão Térmico',
  'Comodidades para passar roupa',
  'Computador',
  'Cozinha',
  'Cozinha Americana',
  'Cozinha Compartilhada',
  'Cozinha Completa',
  'Cozinha compacta',
  'Deck Externo',
  'Deck com Espreguiçadeiras',
  'Deck Externo com poltronas',
  'Despertador',
  'Detector de monóxido de carbono',
  'Dockstation',
  'Ducha',
  'Ducha à gás',
  'Ducha Dupla',
  'Ducha higiênica',
  'Ducha vertical com hidro',
  'DVD Player',
  'Entrada privativa',
  'Espelho de maquiagem',
  'Estacionamento',
  'Estacionamento gratuito',
  'Extintor de incêndio',
  'Fax',
  'Ferro de passar roupa',
  'Flat particular em prédio',
  'Fogão',
  'Fogão à lenha',
  'Fogão Cooktop 2 bocas',
  'Fogão Cooktop 4 bocas',
  'Fogão de indução duas bocas',
  'Fogão elétrico - 2 queimadores',
  'Fogo de chão externo',
  'Fogueira',
  'Forno',
  'Forno Elétrico',
  'Frigobar',
  'Garagem Privativa',
  'Garrafa térmica',
  'Geladeira',
  'Geladeira para bebidas',
  'Geminado',
  'Gerador de Energia',
  'Grades de segurança para bebês',
  'Guarda-roupa ou armário',
  'Hidromassagem',
  'Hidromassagem Casal',
  'Hidromassagem na varanda',
  'Hidromassagem para uma pessoa',
  'Hidromassagem vertical',
  'Hidromassagem Vista Panorâmica',
  'Horta',
  'Independente',
  'Instalações hipoalergênicas',
  'Interfone',
  'Internet sem fio (Wi-Fi)',
  'Isolamento acústico',
  'Jacuzzi',
  'Jardim Privativo',
  'Jogos de tabuleiro/quebra-cabeças',
  'Kit de primeiros socorros',
  'Kit Escalda Pés',
  'Lareira',
  'Lareira em dois ambientes',
  'Lareira interna e externa',
  'Lareira interna: a lenha',
  'Lava-louças',
  'Lavabo',
  'Lençol térmico',
  'Liquidificador',
  'Livros, DVDs ou música para crianças',
  'Localizado no Piso Superior',
  'Luminária na cabeceira',
  'Máquina de café',
  'Máquina de café Dolce Gusto',
  'Máquina de gelo',
  'Máquina de lavar roupa',
  'Máquina de lavar roupas',
  'Máquina Nespresso',
  'Mesa com duas cadeiras',
  'Mesa de jantar',
  'Mesa de trabalho',
  'Mesa no jardim',
  'Mezanino',
  'Micro-ondas',
  'Microondas',
  'Mixer Processador',
  'Mosquiteiro',
  'Móveis na área externa',
  'Notebook',
  'Ofurô',
  'Ofurô ao ar livre',
  'Ofurô na Varanda',
  'Ofurô no Banheiro',
  'Ofurô no jardim',
  'Pantufas',
  'Papel higiênico',
  'Pé direito duplo',
  'Piscina Aquecida',
  'Piscina com borda infinita',
  'Piscina privativa',
  'Piscina térmica com borda infinita',
  'Piso Aquecido',
  'Piso de Carpete',
  'Piso de madeira',
  'Piso de madeira ou parquet',
  'Piso de mármore/azulejo',
  'Piso Frio',
  'Pole Dance',
  'Poltrona',
  'Prancha de cabelo',
  'Prensa para calças',
  'Produtos de higiene pessoal gratuitos',
  'Produtos de limpeza',
  'Projetor com Netflix',
  'Protetores de tomadas',
  'Purificadores de ar',
  'Quadra de beach tennis',
  'Quarto(s) conjugado(s) disponível(is)',
  'Rádio',
  'Recepção 24 horas',
  'Roupa de cama',
  'Roupa de cama e banho',
  'Roupa de cama e banho (custo adicional)',
  'Roupa de Cama Especial',
  'Roupão',
  'Roupão de Banho',
  'Sacada com tela de proteção',
  'Sacada com vista aprazível para a natureza',
  'Sacada privativa',
  'Sala de estar',
  'Sala de jantar',
  'Sanduicheira',
  'Sauna',
  'Sauna seca e úmida privativa',
  'Secador de Cabelo',
  'Secadora de roupas',
  'Serviço de camareira',
  'Serviço de despertar',
  'Serviço de despertar/despertador',
  'Serviço de lavanderia',
  'Serviço de Quarto',
  'Serviço de Streaming',
  'Serviço de streaming (como Netflix)',
  'Sistema de som Alexa',
  'Situada no piso inferior e superior',
  'Smart TV',
  'Smart TV 32 com Sky',
  'Smart TV 48 com pacote completo Sky',
  'Sofá',
  'Sofá-cama',
  'Sofá-Cama / 2 pessoas',
  'SPA com hidromassagem, cromoterapia e aquecimento',
  'Stand-Up',
  'Suporte para mala',
  'Telefone',
  'Televisão',
  'Televisão de LED',
  'Terraço',
  'Terraço com rede',
  'Terraço com vista aprazível',
  'Terraço privativo com hidromassagem',
  'Teto panorâmico',
  'Toalhas',
  'Toalete',
  'Toalete adicional',
  'Toalete compartilhado',
  'Tomada perto da cama',
  'Torradeira',
  'Travesseiro',
  'TV',
  'TV a cabo',
  'TV de tela plana',
  'TV LCD',
  'TV LED na sala',
  'TV LED no quarto',
  'TV(s) 60 SKY HDTV, DVD, NETFLIX',
  'Utensílios de cozinha',
  'Varanda',
  'Varanda com churrasqueira',
  'Varanda com deck',
  'Varanda com Rede',
  'Varanda térrea',
  'Varal de Parede no Banheiro',
  'Varal de Parede no externo',
  'Varal para secar roupas',
  'Varal Retrátil na Varanda',
  'Vaso sanitário',
  'Ventilador',
  'Ventilador de Teto',
  'Ventilador de Teto no quarto',
  'Videogame',
  'Videogame - Nintendo Wii',
  'Videogame - PlayStation 3',
  'Videogame - Xbox 360',
  'Videogames',
  'Vista',
  'Vista da cidade',
  'Vista da montanha',
  'Vista da piscina',
  'Vista de um local de interesse',
  'Vista do jardim',
  'Vista do lago',
  'Vista do mar',
  'Vista do pátio interno',
  'Vista do rio',
  'Vista Para Rua',
  'Vista para a Cidade',
  'Vista para a entrada',
  'Vista para a lagoa',
  'Vista para a Mata Atlântica',
  'Vista para a montanha',
  'Vista para a piscina',
  'Vista para jardins',
  'Vista para o mar',
  'Vista para o Mar',
  'Vista para o rio',
  'Vista para o vale',
  'Voltagem 110V',
  'Voltagem 110V e 220V',
  'Voltagem 220V',
  'Wifi',
  'WiFi Gratuito no Quarto',
  '2 Camas de Solteiro',
  '2 Chuveiros',
  'Aquecedor de toalhas',
  'Bicama',
].sort();

const filteredAmenities = computed(() => {
  if (!amenitySearch.value.trim()) return amenitiesCatalog;
  const q = amenitySearch.value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return amenitiesCatalog.filter(a =>
    a
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .includes(q)
  );
});

const bedTypeOptions = [
  'Cama Casal',
  'Cama Queen Size',
  'Cama King Size',
  'Cama Super King Size',
  'Cama Solteiro',
  'Cama Box Solteiro',
  'Cama Box Casal',
  'Cama Box Queen',
  'Bicama',
  'Beliche',
  'Sofá-cama',
  'Cama dobrável',
  'Cama auxiliar',
  'Berço',
  'Cama extra-longa (+ de 2m)',
];
</script>

<template>
  <form
    class="flex flex-col gap-4 max-h-[80vh] overflow-auto"
    @submit.prevent="handleSubmit"
  >
    <div class="text-lg font-medium text-slate-800 dark:text-slate-100">
      {{
        room.id
          ? t('MOTOR_RESERVA.ROOMS.EDIT.TITLE')
          : t('MOTOR_RESERVA.ROOMS.ADD.TITLE')
      }}
    </div>

    <!-- Indicador de Unidade vinculada -->
    <div
      v-if="room.parent_name"
      class="flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700"
    >
      <span class="text-sm text-blue-700 dark:text-blue-300">
        Unidade de: <strong>{{ room.parent_name }}</strong>
      </span>
    </div>

    <!-- Tabs -->
    <div
      class="flex gap-1 border-b border-slate-200 dark:border-slate-600 overflow-x-auto"
    >
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        class="px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap"
        :class="
          activeTab === tab.key
            ? 'border-woot-500 text-woot-500'
            : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
        "
        @click="activeTab = tab.key"
      >
        {{ tab.label }}
        <span
          v-if="tab.key === 'amenities' && selectedAmenities.length"
          class="ml-1 text-xs bg-woot-100 text-woot-600 px-1.5 py-0.5 rounded-full"
        >
          {{ selectedAmenities.length }}
        </span>
      </button>
    </div>

    <!-- Tab: Dados Gerais -->
    <div v-show="activeTab === 'general'" class="flex flex-col gap-4">
      <!-- Tipo e Status -->
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
          >
            Tipo
          </label>
          <select v-model="roomType" :class="ic">
            <option value="">Selecione o tipo...</option>
            <option v-for="rt in roomTypeOptions" :key="rt" :value="rt">
              {{ rt }}
            </option>
          </select>
        </div>
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
          >
            Situação
          </label>
          <select v-model="status" :class="ic">
            <option
              v-for="opt in statusOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>
      </div>

      <!-- Nome e Número -->
      <div class="grid grid-cols-3 gap-4">
        <div class="col-span-2">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
          >
            Identificação (Nome)
          </label>
          <input
            v-model="name"
            type="text"
            :class="ic"
            placeholder="Ex: Suíte Master, Quarto Standard..."
          />
        </div>
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
          >
            Número
          </label>
          <input
            v-model="roomNumber"
            type="text"
            :class="ic"
            placeholder="Ex: 101, A-1..."
          />
        </div>
      </div>

      <!-- Slogan -->
      <div>
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
        >
          Slogan
        </label>
        <input
          v-model="slogan"
          type="text"
          :class="ic"
          placeholder="Frase curta de divulgação..."
        />
      </div>

      <!-- Vinculação (Parent) -->
      <div v-if="availableParents.length">
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
        >
          Vinculado a (Imóvel Pai)
        </label>
        <select v-model="parentId" :class="ic">
          <option :value="null">Nenhum (imóvel independente)</option>
          <option v-for="p in availableParents" :key="p.id" :value="p.id">
            {{ p.name }}
            <template v-if="p.room_type"> ({{ p.room_type }})</template>
          </option>
        </select>
        <p class="text-xs text-slate-500 mt-1">
          Vincule como Unidade Habitacional de um Hotel/Pousada.
        </p>
      </div>

      <!-- Ref Externa TDF -->
      <div>
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
        >
          Ref Motor TDF (referência externa)
        </label>
        <input
          v-model="externalRef"
          type="text"
          :class="ic"
          placeholder="Código de referência no TDF..."
        />
      </div>

      <!-- YouTube -->
      <div>
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
        >
          Link Vídeo no YouTube (embed)
        </label>
        <input
          v-model="youtubeUrl"
          type="text"
          :class="ic"
          placeholder="https://www.youtube.com/embed/..."
        />
      </div>

      <!-- Descrição Rica -->
      <div>
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
        >
          Texto Descritivo
        </label>
        <textarea
          v-model="richDescription"
          rows="4"
          :class="ic"
          placeholder="Descreva o quarto em detalhes..."
        />
      </div>

      <!-- Ocupação -->
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
          >
            Ocupação Mínima
          </label>
          <input
            v-model.number="minOccupancy"
            type="number"
            min="1"
            :class="ic"
          />
        </div>
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
          >
            Ocupação Máxima
          </label>
          <input
            v-model.number="maxOccupancy"
            type="number"
            min="1"
            :class="ic"
          />
        </div>
      </div>

      <div class="grid grid-cols-2 gap-4">
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
          >
            Máximo Adultos
          </label>
          <input v-model.number="maxAdults" type="number" min="1" :class="ic" />
        </div>
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
          >
            Máximo Crianças
          </label>
          <input
            v-model.number="maxChildren"
            type="number"
            min="0"
            :class="ic"
          />
        </div>
      </div>

      <!-- Total de Unidades -->
      <div>
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
        >
          Total de Unidades
        </label>
        <template v-if="!room.parent_id && room.id && unitsCount > 0">
          <div class="flex items-center gap-2">
            <span
              class="px-3 py-2 text-sm font-medium text-woot-600 bg-woot-50 dark:bg-woot-900/20 dark:text-woot-400 rounded-lg"
            >
              {{ activeUnitsCount }}
            </span>
            <span class="text-xs text-slate-500">
              {{ t('MOTOR_RESERVA.ROOMS.UNITS.AUTO_CALCULATED') }}
            </span>
          </div>
        </template>
        <input
          v-else
          v-model.number="totalUnits"
          type="number"
          min="1"
          :class="ic"
        />
      </div>

      <!-- Checkboxes -->
      <div class="flex items-center gap-4">
        <label
          class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
        >
          <input
            v-model="allowsSmokers"
            type="checkbox"
            class="rounded border-slate-300 text-woot-500"
          />
          Permite fumantes
        </label>
        <label
          class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
        >
          <input
            v-model="available"
            type="checkbox"
            class="rounded border-slate-300 text-woot-500"
          />
          Disponível
        </label>
      </div>
    </div>

    <!-- Tab: Fotos -->
    <div v-show="activeTab === 'photos'" class="flex flex-col gap-4">
      <div v-if="room.id" class="flex flex-col gap-3">
        <label
          class="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-slate-300 dark:border-slate-500 rounded-lg cursor-pointer hover:border-woot-500 transition-colors"
          :class="{ 'opacity-50 pointer-events-none': isUploadingPhoto }"
        >
          <span class="text-sm text-slate-600 dark:text-slate-300">
            <template v-if="isUploadingPhoto">
              Enviando {{ uploadProgress.current }}/{{
                uploadProgress.total
              }}...
            </template>
            <template v-else>
              Clique para selecionar fotos (pode selecionar várias)
            </template>
          </span>
          <input
            type="file"
            accept="image/*"
            multiple
            class="hidden"
            @change="onFileSelected"
          />
        </label>
        <p class="text-xs text-slate-500">
          As fotos são salvas automaticamente no MinIO/S3. Use as setas para
          reordenar.
        </p>
      </div>

      <div
        v-else
        class="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-700"
      >
        <p class="text-sm text-amber-700 dark:text-amber-300">
          Salve o quarto primeiro para poder adicionar fotos.
        </p>
      </div>

      <div v-if="photos.length" class="flex flex-col gap-2">
        <div
          v-for="(photo, idx) in photos"
          :key="photo.id"
          class="flex items-center gap-3 p-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800"
        >
          <img
            :src="photo.url"
            :alt="photo.description || 'Foto'"
            class="w-20 h-14 object-cover rounded"
          />
          <div class="flex-1 min-w-0">
            <p class="text-sm text-slate-700 dark:text-slate-200 truncate">
              {{ photo.description || `Foto ${idx + 1}` }}
            </p>
            <span
              class="text-xs px-1.5 py-0.5 rounded"
              :class="
                photo.show_in_ad !== false
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
              "
            >
              {{ photo.show_in_ad !== false ? 'No anúncio' : 'Oculta' }}
            </span>
          </div>
          <div class="flex items-center gap-1">
            <button
              type="button"
              class="w-7 h-7 flex items-center justify-center rounded text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30"
              :disabled="idx === 0"
              title="Subir"
              @click="movePhoto(idx, -1)"
            >
              ↑
            </button>
            <button
              type="button"
              class="w-7 h-7 flex items-center justify-center rounded text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30"
              :disabled="idx === photos.length - 1"
              title="Descer"
              @click="movePhoto(idx, 1)"
            >
              ↓
            </button>
            <button
              type="button"
              class="w-7 h-7 flex items-center justify-center rounded hover:bg-blue-50 dark:hover:bg-blue-900/30"
              :class="
                photo.show_in_ad !== false ? 'text-blue-500' : 'text-slate-400'
              "
              title="Toggle exibição no anúncio"
              @click="togglePhotoAd(photo)"
            >
              ●
            </button>
            <button
              type="button"
              class="w-7 h-7 flex items-center justify-center rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30"
              title="Remover"
              @click="removePhoto(photo)"
            >
              ✕
            </button>
          </div>
        </div>
      </div>

      <p
        v-if="room.id && !photos.length"
        class="text-sm text-slate-500 text-center py-4"
      >
        Nenhuma foto adicionada ainda.
      </p>
    </div>

    <!-- Tab: Comodidades -->
    <div v-show="activeTab === 'amenities'" class="flex flex-col gap-3">
      <div class="flex items-center justify-between">
        <p class="text-sm text-slate-600 dark:text-slate-300">
          Selecione as comodidades do quarto.
          <span class="font-medium text-woot-500"
            >({{ selectedAmenities.length }}) Selecionadas</span
          >
        </p>
      </div>

      <input
        v-model="amenitySearch"
        type="text"
        :class="ic"
        placeholder="Filtrar comodidades... digite para buscar"
      />

      <div
        class="max-h-[400px] overflow-y-auto border border-slate-200 dark:border-slate-600 rounded-lg p-3"
      >
        <p
          v-if="!filteredAmenities.length"
          class="text-sm text-slate-500 text-center py-4"
        >
          Nenhuma comodidade encontrada para "{{ amenitySearch }}"
        </p>
        <div class="grid grid-cols-2 gap-1">
          <label
            v-for="amenity in filteredAmenities"
            :key="amenity"
            class="flex items-center gap-2 py-1 px-2 rounded text-sm cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700"
            :class="
              selectedAmenities.includes(amenity)
                ? 'text-woot-600 font-medium bg-woot-50 dark:bg-woot-900/20'
                : 'text-slate-700 dark:text-slate-300'
            "
          >
            <input
              type="checkbox"
              :checked="selectedAmenities.includes(amenity)"
              class="rounded border-slate-300 text-woot-500 focus:ring-woot-500"
              @change="toggleAmenity(amenity)"
            />
            {{ amenity }}
          </label>
        </div>
      </div>
    </div>

    <!-- Tab: Tipos de Cama -->
    <div v-show="activeTab === 'beds'" class="flex flex-col gap-4">
      <div class="flex gap-2 items-end">
        <div class="flex-1">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
          >
            Tipo de Cama
          </label>
          <select v-model="newBedType" :class="ic">
            <option value="" disabled>Selecione...</option>
            <option v-for="bt in bedTypeOptions" :key="bt" :value="bt">
              {{ bt }}
            </option>
          </select>
        </div>
        <div class="w-24">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
          >
            Qtd
          </label>
          <input v-model.number="newBedQty" type="number" min="1" :class="ic" />
        </div>
        <Button label="Adicionar" size="sm" type="button" @click="addBedType" />
      </div>

      <div v-if="bedTypes.length" class="flex flex-col gap-2">
        <div
          v-for="(bed, idx) in bedTypes"
          :key="idx"
          class="flex items-center justify-between px-3 py-2 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-600"
        >
          <span class="text-sm text-slate-800 dark:text-slate-100">
            {{ bed.quantity }}x {{ bed.name }}
          </span>
          <button
            type="button"
            class="text-sm text-red-500 hover:text-red-700"
            @click="removeBedType(idx)"
          >
            Remover
          </button>
        </div>
      </div>

      <p v-else class="text-sm text-slate-500 text-center py-8">
        Nenhum tipo de cama adicionado ainda.
      </p>
    </div>

    <!-- Ações -->
    <div
      class="flex items-center justify-end gap-2 pt-4 border-t border-slate-200 dark:border-slate-600"
    >
      <Button
        :label="t('MOTOR_RESERVA.ROOMS.FORM.CANCEL')"
        variant="secondary"
        size="sm"
        @click="emit('close')"
      />
      <Button
        :label="t('MOTOR_RESERVA.ROOMS.FORM.SUBMIT')"
        size="sm"
        :disabled="!isFormValid || isSubmitting"
        :is-loading="isSubmitting"
        @click="handleSubmit"
      />
    </div>
  </form>
</template>
